import {
	Badge,
	ButtonUtility,
	FilterBar,
	LoadingIndicator,
	TABLE_LISTING_STYLES,
	Table,
	TableCard,
	type TableColumn,
	Tabs,
} from "@components";
import { useQuery } from "@tanstack/react-query";
import { Download01, Edit05, LinkExternal01, Plus } from "@untitledui/icons";
import dayjs from "dayjs";
import { type ComponentPropsWithRef, type FC, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/base/buttons/button";
import type { VehicleDocument } from "@/features/vehicle/types/vehicle-document.types";
import { cx } from "@/utils/cx";
import { DOCUMENTOS_SUB_TABS } from "../constants/vehicle-tabs.constant";
import {
	vehicleDocumentStatusColor,
	vehicleDocumentStatusDotColor,
	vehicleDocumentStatusLabel,
	vehicleDocumentTypeIdLabel,
} from "../handlers";
import { useVehicleDocumentUrl } from "../hooks";
import { getVehicleDocumentsCurrent } from "../services";
import type { VehicleDocumentStatus } from "../types";
import { VehicleDocumentEmpty } from "./vehicle-document-empty";

function formatDisplayDate(iso: string): string {
	if (!iso) {
		return "-";
	}
	const [y, m, d] = iso.split("-").map(Number);
	if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
		return "-";
	}
	return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
}

type VehicleDocumentsMainTableProps = {
	onAdd: () => void;
	onEdit: (doc: VehicleDocument) => void;
};

function noopSelection(_keys: Set<React.Key> | "all") {
	/* selection disabled */
}

export function VehicleDocumentsMainTable({
	onAdd,
	onEdit,
}: VehicleDocumentsMainTableProps) {
	const [documentosSubTab, setDocumentosSubTab] = useState<
		"principais" | "outros"
	>("principais");

	const [searchParams] = useSearchParams();
	const vehicleId = searchParams.get("id") || "";
	const { mutate: getVehicleDocumentUrl } = useVehicleDocumentUrl();

	const vehicleDocumentsCurrent = useQuery({
		queryKey: ["vehicle-documents-current", vehicleId],
		queryFn: () => getVehicleDocumentsCurrent(Number(vehicleId)),
		initialData: {
			mainDocuments: [],
			otherDocuments: [],
		},
		enabled: !!vehicleId,
		refetchOnWindowFocus: false,
	});

	const filteredDocuments = useMemo(() => {
		const list =
			documentosSubTab === "outros"
				? vehicleDocumentsCurrent.data?.otherDocuments
				: vehicleDocumentsCurrent.data?.mainDocuments;

		return list;
	}, [
		documentosSubTab,
		vehicleDocumentsCurrent.data?.mainDocuments,
		vehicleDocumentsCurrent.data?.otherDocuments,
	]);

	const columnsMainActions: TableColumn<VehicleDocument>[] = useMemo(() => {
		const columnsMainActionsButton = (
			row: VehicleDocument,
			Icon: FC<ComponentPropsWithRef<"svg">>,
			onClick: (document: VehicleDocument) => void,
			tooltip: string
		) => (
			<ButtonUtility
				className="text-primary"
				color="tertiary"
				icon={Icon}
				onClick={() => onClick(row)}
				size="xs"
				tooltip={tooltip}
			/>
		);

		return [
			{
				id: "actions",
				label: "Ações",
				render: (row) => (
					<div className="flex items-center gap-1">
						{columnsMainActionsButton(row, Edit05, onEdit, "Editar")}
						{columnsMainActionsButton(
							row,
							LinkExternal01,
							() =>
								getVehicleDocumentUrl({
									vehicleId,
									documentId: row.id,
									openUrl: true,
								}),
							"Visualizar"
						)}
						{columnsMainActionsButton(
							row,
							Download01,
							() =>
								getVehicleDocumentUrl({
									vehicleId,
									documentId: row.id,
								}),
							"Download"
						)}
					</div>
				),
			},
		];
	}, [onEdit, getVehicleDocumentUrl, vehicleId]);

	const columnsPrincipais: TableColumn<VehicleDocument>[] = useMemo(
		() => [
			{
				id: "type",
				label: "Tipo de Documento",
				minWidth: 140,
				render: (row) => vehicleDocumentTypeIdLabel(row.type),
			},
			{
				id: "createdAt",
				label: "Data de Cadastro",
				render: (row) => dayjs(row.createdAt).format("DD/MM/YYYY"),
			},
			{
				id: "paymentDate",
				label: "Data de Pagamento",
				render: (row) =>
					row.paymentDate ? dayjs(row.paymentDate).format("DD/MM/YYYY") : "-",
			},
			{
				id: "renewalDate",
				label: "Próxima Renovação",
				render: (row) =>
					row.renewalDate ? dayjs(row.renewalDate).format("DD/MM/YYYY") : "-",
			},
			{
				id: "situation",
				label: "Situação",
				render: (row) => (
					<Badge
						className={cx(
							"rounded-full px-1.5 py-2 font-medium leading-3",
							vehicleDocumentStatusColor(row?.status as VehicleDocumentStatus)
						)}
					>
						<div
							className={cx(
								"size-1.5 rounded-full",
								vehicleDocumentStatusDotColor(
									row?.status as VehicleDocumentStatus
								)
							)}
						/>
						{vehicleDocumentStatusLabel(row?.status as VehicleDocumentStatus)}
					</Badge>
				),
			},
			...columnsMainActions,
		],
		[columnsMainActions]
	);

	const columnsOutros: TableColumn<VehicleDocument>[] = useMemo(
		() => [
			{
				id: "type",
				label: "Tipo de Documento",
				minWidth: 140,
				render: (row) => vehicleDocumentTypeIdLabel(row.type),
			},
			{
				id: "createdAt",
				label: "Data de Cadastro",
				render: (row) => dayjs(row.createdAt).format("DD/MM/YYYY"),
			},
			{
				id: "paymentDate",
				label: "Data de Pagamento",
				render: (row) => formatDisplayDate(row.paymentDate ?? ""),
			},
			{
				id: "renewalDate",
				label: "Próxima Renovação",
				render: (row) => formatDisplayDate(row.renewalDate),
			},
			{
				id: "description",
				label: "Descrição",
				render: (row) => (
					<p className="text-placeholder text-sm">{row.description ?? "-"}</p>
				),
			},
			...columnsMainActions,
		],
		[columnsMainActions]
	);

	const columns =
		documentosSubTab === "outros" ? columnsOutros : columnsPrincipais;

	return (
		<div className="mb-8">
			<div className="flex xs:flex-row flex-col items-center justify-between gap-3 border-border-secondary border-b px-6">

				<FilterBar label="container-actions" className="w-full">
					<Tabs
						defaultSelectedKey={documentosSubTab}
						onSelectionChange={(tabId) =>
							setDocumentosSubTab(tabId as "principais" | "outros")
						}
						selectedKey={documentosSubTab}
						className='flex-1'
					>
						<Tabs.List
							className="w-fit flex-row gap-0 overflow-hidden rounded-lg border border-border-primary"
							items={DOCUMENTOS_SUB_TABS}
							type="button-gray"
						>
							{(tab) => (
								<Tabs.Item
									{...tab}
									className="rounded-none border-border-primary not-last:border-r font-semibold aria-selected:bg-secondary"
								/>
							)}
						</Tabs.List>
					</Tabs>

					<Button
						className="ml-auto w-full sm:max-w-fit"
						iconLeading={Plus}
						onClick={onAdd}
						size="md"
					>
						Adicionar Documento
					</Button>
				</FilterBar>
			</div>

			<TableCard.Root className="mx-6 mt-3" variant="listing">
				<Table
					aria-label="Documentos"
					onSelectionChange={noopSelection}
					selectedKeys={new Set()}
					selectionMode="none"
				>
					<Table.Header columns={columns}>
						{(column) => (
							<Table.Head
								id={column.id}
								isRowHeader={columns.indexOf(column) === 0}
								key={column.id}
								label={column.label}
								style={{
									...(!!column?.minWidth && {
										minWidth: `${column.minWidth}px`,
									}),
								}}
							/>
						)}
					</Table.Header>

					<Table.Body>
						{vehicleDocumentsCurrent.isFetching ? (
							<Table.Row>
								<Table.Cell
									className={TABLE_LISTING_STYLES.stateCell}
									colSpan={columns.length}
								>
									<LoadingIndicator size="md" type="line-simple" />
								</Table.Cell>
							</Table.Row>
						) : (
							filteredDocuments.length > 0 &&
							filteredDocuments.map((item) => (
								<Table.Row columns={columns} id={item.id} key={item.id}>
									{(column) => (
										<Table.Cell className="whitespace-nowrap" key={column.id}>
											{column.render
												? column.render(item)
												: String(
														item[column.id as keyof VehicleDocument] ?? ""
													)}
										</Table.Cell>
									)}
								</Table.Row>
							))
						)}
						{filteredDocuments?.length === 0 &&
							!vehicleDocumentsCurrent.isFetching && (
								<Table.Row>
									<Table.Cell
										className={TABLE_LISTING_STYLES.stateCell}
										colSpan={columns.length}
									>
										<VehicleDocumentEmpty
											buttonText="Adicionar Documento"
											description="Clique no botão abaixo para adicionar o primeiro documento."
											onAdd={onAdd}
											title="Nenhum documento cadastrado!"
										/>
									</Table.Cell>
								</Table.Row>
							)}
					</Table.Body>
				</Table>
			</TableCard.Root>
		</div>
	);
}
